/**
 * Central QueryClient — SWR pattern with LOCALSTORAGE PERSISTENCE.
 *
 * Turn CC · 14 — Performance UX P1.
 *
 * Behaviour targets (Notion/Linear-grade):
 *  - Cold first login  → skeleton visible for ~200-500 ms while first fetch
 *    resolves.
 *  - Any subsequent page load (refresh, tab restore, hard reload) →
 *    instant paint from localStorage cache, silent background revalidation.
 *  - Row hover → detail is prefetched; click = 0 ms perceived nav.
 *  - Transient 5xx → auto-retry x2 with exponential backoff, then a
 *    professional empty state (no raw `HTTP 500` on screen).
 *
 * Cache tuning:
 *  - staleTime: 5 min for list views (they don't change often; auto brief
 *    cron writes once per day / week).
 *  - gcTime:   30 min (kept for back-nav + tab-switch instantness).
 *  - persist:  localStorage, `hm-rq-cache` v1, 24 h max age.
 */
import { QueryClient } from "@tanstack/react-query";
import { API, apiJson, authHeaders } from "@/lib/api";

// --- Retry policy ---
// Deterministic exponential backoff: 400 ms → 1200 ms → give up.
// Only retry on network / 5xx (react-query passes the caught error).
function _retry(failureCount, error) {
  if (failureCount >= 2) return false;
  const status = error?.status ?? 0;
  // 4xx (except 429) → user's problem, don't loop.
  if (status >= 400 && status < 500 && status !== 429) return false;
  return true;
}
function _retryDelay(attempt) {
  return Math.min(400 * Math.pow(3, attempt), 1200);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Turn CC · 14 — List views + entity details are stable enough that
      // 5 min stale window makes navigation feel INSTANT.
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: _retry,
      retryDelay: _retryDelay,
    },
    mutations: {
      retry: _retry,
      retryDelay: _retryDelay,
    },
  },
});

// ---------- Fetchers ----------

async function _json(path) {
  return apiJson(`${API}${path}`, { headers: authHeaders() });
}

export const fetchers = {
  topics:            () => _json(`/topics`),
  markets:           () => _json(`/markets`),
  segments:          () => _json(`/segments`),
  companies: (params = {}) => {
    const p = new URLSearchParams();
    if (params.search) p.set("search", params.search);
    if (params.priority) p.set("priority", params.priority);
    p.set("limit", String(params.limit ?? 100));
    return _json(`/companies?${p}`);
  },
  editorialProposals: (params = {}) => {
    const p = new URLSearchParams({
      status: params.status ?? "pending",
      min_confidence: String(params.minConf ?? 0),
      limit: String(params.limit ?? 50),
    });
    return _json(`/editorial/proposals?${p}`);
  },
  sources: () => _json(`/sources?limit=1000`),
  dailyBriefs: (params = {}) => {
    const p = new URLSearchParams({ limit: String(params.limit ?? 50) });
    if (params.source) p.set("source", params.source);
    if (params.status) p.set("status", params.status);
    return _json(`/daily-briefs?${p}`);
  },
  dailyBriefDetail: (id) => _json(`/daily-briefs/${id}`),
  dailyBriefRunInsights: (id) => _json(`/daily-brief-v2/${id}/run-insights`),
  dailyBriefSendHistory: (id) => _json(`/daily-brief-v2/${id}/send-history`),
  weeklyBriefs: () => _json(`/weekly-brief-v2?limit=30`),
  weeklyBriefDetail: (id) => _json(`/weekly-brief-v2/${id}`),
  weeklyBriefSendHistory: (id) => _json(`/weekly-brief-v2/${id}/send-history`),
  monthlyBriefs: () => _json(`/monthly-brief-v2?limit=30`),
  monthlyBriefDetail: (id) => _json(`/monthly-brief-v2/${id}`),
  monthlyBriefSendHistory: (id) => _json(`/monthly-brief-v2/${id}/send-history`),
  weeklyQuotes: (params = {}) => {
    const p = new URLSearchParams({ limit: String(params.limit ?? 30) });
    if (params.source) p.set("source", params.source);
    return _json(`/weekly-quotes-v2?${p}`);
  },
  weeklyQuotesDetail: (id) => _json(`/weekly-quotes-v2/${id}`),
  weeklyQuotesSendHistory: (id) => _json(`/weekly-quotes-v2/${id}/send-history`),
  deliverySettings: () => _json(`/daily-brief-v2/delivery-settings`),
  schedulerStatus: () => _json(`/scheduler/status`),
  meIntegrations: () => _json(`/me/integrations`),
  governanceCharter: (product) =>
    _json(`/governance/charter${product ? `?product=${encodeURIComponent(product)}` : ""}`),
  blockedSources: () => _json(`/governance/blocked-sources`),
  emergingTopics: () => _json(`/insights/emerging-topics?window_hours=168`),
  knowledge: (params = {}) => {
    const p = new URLSearchParams();
    if (params.q) p.set("q", params.q);
    p.set("limit", String(params.limit ?? 200));
    return _json(`/knowledge/graph?${p}`);
  },
  memoryStats: () => _json(`/memory/stats`),
  memoryEntries: (kind = "short_term", limit = 50) =>
    _json(`/memory/entries?kind=${kind}&limit=${limit}`),
  agents: () => _json(`/agents`),
  workflows: () => _json(`/workflows`),
  logs: (params = {}) => {
    const p = new URLSearchParams();
    if (params.level) p.set("level", params.level);
    if (params.agent) p.set("agent", params.agent);
    p.set("limit", String(params.limit ?? 100));
    return _json(`/logs?${p}`);
  },
  featureFlags: () => _json(`/feature-flags`),
  settingsSecrets: () => _json(`/settings/secrets`),
  pushDeadLetter: () => _json(`/push/dead-letter?limit=20`),
  authMe: () => _json(`/auth/me`),
  // Phase 7 — backbone-scoped article lists
  companyArticles: (slug, params = {}) =>
    _json(`/companies/${encodeURIComponent(slug)}/articles?${_qs(params)}`),
  topicArticles: (slug, params = {}) =>
    _json(`/topics/${encodeURIComponent(slug)}/articles?${_qs(params)}`),
  marketArticles: (slug, params = {}) =>
    _json(`/markets/${encodeURIComponent(slug)}/articles?${_qs(params)}`),
  companyDetail: (slug) => _json(`/companies/${encodeURIComponent(slug)}`),
  topicDetail:   (slug) => _json(`/topics/${encodeURIComponent(slug)}`),
  marketDetail:  (slug) => _json(`/markets/${encodeURIComponent(slug)}`),
  reasoningEntity: (kind, slug) =>
    _json(`/reasoning/entity/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}`),
  reasoningOverview: () => _json(`/reasoning/overview/latest`),
};

function _qs(obj) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  }
  return p.toString();
}

// ---------- Query keys ----------
export const qk = {
  companies:          (params) => ["companies", params || {}],
  topics:             () => ["topics"],
  markets:            () => ["markets"],
  segments:           () => ["segments"],
  editorialProposals: (params) => ["editorial", "proposals", params || {}],
  sources:            () => ["sources"],
  dailyBriefs:        (params) => ["daily-briefs", params || {}],
  dailyBriefDetail:   (id) => ["daily-briefs", id],
  dailyBriefRunInsights: (id) => ["daily-briefs", id, "run-insights"],
  dailyBriefSendHistory: (id) => ["daily-briefs", id, "send-history"],
  weeklyBriefs:       () => ["weekly-briefs"],
  weeklyBriefDetail:  (id) => ["weekly-briefs", id],
  weeklyBriefSendHistory: (id) => ["weekly-briefs", id, "send-history"],
  monthlyBriefs:       () => ["monthly-briefs"],
  monthlyBriefDetail:  (id) => ["monthly-briefs", id],
  monthlyBriefSendHistory: (id) => ["monthly-briefs", id, "send-history"],
  weeklyQuotes:        (params) => ["weekly-quotes", params || {}],
  weeklyQuotesDetail:  (id) => ["weekly-quotes", id],
  weeklyQuotesSendHistory: (id) => ["weekly-quotes", id, "send-history"],
  deliverySettings:   () => ["delivery-settings"],
  schedulerStatus:    () => ["scheduler", "status"],
  meIntegrations:     () => ["me", "integrations"],
  governanceCharter:  (product) => ["governance", "charter", product || null],
  blockedSources:     () => ["governance", "blocked-sources"],
  emergingTopics:     () => ["insights", "emerging-topics", 168],
  knowledge:          (params) => ["knowledge", params || {}],
  memoryStats:        () => ["memory", "stats"],
  memoryEntries:      (kind) => ["memory", "entries", kind],
  agents:             () => ["agents"],
  workflows:          () => ["workflows"],
  logs:               (params) => ["logs", params || {}],
  featureFlags:       () => ["settings", "feature-flags"],
  settingsSecrets:    () => ["settings", "secrets"],
  pushDeadLetter:     () => ["settings", "push-dead-letter"],
  authMe:             () => ["auth", "me"],
  companyArticles: (slug, params) => ["companies", slug, "articles", params || {}],
  topicArticles:   (slug, params) => ["topics",    slug, "articles", params || {}],
  marketArticles:  (slug, params) => ["markets",   slug, "articles", params || {}],
  companyDetail: (slug) => ["companies", slug, "detail"],
  topicDetail:   (slug) => ["topics",    slug, "detail"],
  marketDetail:  (slug) => ["markets",   slug, "detail"],
  reasoningEntity:   (kind, slug) => ["reasoning", kind, slug],
  reasoningOverview: () => ["reasoning", "overview", "latest"],
};

// ---------- Row-level detail prefetch (used on brief-row hover) ----------
export function prefetchDailyBriefDetail(id) {
  if (!id) return;
  try {
    queryClient.prefetchQuery({
      queryKey: qk.dailyBriefDetail(id),
      queryFn: () => fetchers.dailyBriefDetail(id),
      staleTime: 60_000,
    });
  } catch { /* ignore */ }
}
export function prefetchWeeklyBriefDetail(id) {
  if (!id) return;
  try {
    queryClient.prefetchQuery({
      queryKey: qk.weeklyBriefDetail(id),
      queryFn: () => fetchers.weeklyBriefDetail(id),
      staleTime: 60_000,
    });
  } catch { /* ignore */ }
}
export function prefetchMonthlyBriefDetail(id) {
  if (!id) return;
  try {
    queryClient.prefetchQuery({
      queryKey: qk.monthlyBriefDetail(id),
      queryFn: () => fetchers.monthlyBriefDetail(id),
      staleTime: 60_000,
    });
  } catch { /* ignore */ }
}
export function prefetchWeeklyQuotesDetail(id) {
  if (!id) return;
  try {
    queryClient.prefetchQuery({
      queryKey: qk.weeklyQuotesDetail(id),
      queryFn: () => fetchers.weeklyQuotesDetail(id),
      staleTime: 60_000,
    });
  } catch { /* ignore */ }
}

// ---------- Backbone drawer hover prefetch ----------
export function prefetchEntityArticles(kind, slug) {
  if (!kind || !slug) return;
  const map = {
    company: { key: qk.companyArticles(slug, { limit: 20 }), fn: () => fetchers.companyArticles(slug, { limit: 20 }) },
    topic:   { key: qk.topicArticles(slug,   { limit: 20 }), fn: () => fetchers.topicArticles(slug,   { limit: 20 }) },
    market:  { key: qk.marketArticles(slug,  { limit: 20 }), fn: () => fetchers.marketArticles(slug,  { limit: 20 }) },
  };
  const entry = map[kind];
  if (!entry) return;
  try {
    queryClient.prefetchQuery({ queryKey: entry.key, queryFn: entry.fn });
  } catch { /* ignore */ }
}

// ---------- Sidebar hover prefetch map ----------
export function prefetchByPath(to) {
  const rules = {
    "/companies":    () => {
      queryClient.prefetchQuery({ queryKey: qk.companies(), queryFn: () => fetchers.companies() });
      queryClient.prefetchQuery({ queryKey: qk.topics(),    queryFn: fetchers.topics });
      queryClient.prefetchQuery({ queryKey: qk.markets(),   queryFn: fetchers.markets });
      queryClient.prefetchQuery({ queryKey: qk.segments(),  queryFn: fetchers.segments });
    },
    "/editorial":    () => queryClient.prefetchQuery({ queryKey: qk.editorialProposals({ status: "pending", minConf: 0 }), queryFn: () => fetchers.editorialProposals({ status: "pending", minConf: 0 }) }),
    "/sources":      () => queryClient.prefetchQuery({ queryKey: qk.sources(),     queryFn: fetchers.sources }),
    "/daily-briefs": () => queryClient.prefetchQuery({ queryKey: qk.dailyBriefs({ source: "hailmary" }), queryFn: () => fetchers.dailyBriefs({ source: "hailmary" }) }),
    "/weekly-briefs": () => queryClient.prefetchQuery({ queryKey: qk.weeklyBriefs(), queryFn: fetchers.weeklyBriefs }),
    "/monthly-briefs": () => queryClient.prefetchQuery({ queryKey: qk.monthlyBriefs(), queryFn: fetchers.monthlyBriefs }),
    "/weekly-quotes": () => queryClient.prefetchQuery({ queryKey: qk.weeklyQuotes({ source: "hailmary" }), queryFn: () => fetchers.weeklyQuotes({ source: "hailmary" }) }),
    "/knowledge":    () => queryClient.prefetchQuery({ queryKey: qk.knowledge(),   queryFn: () => fetchers.knowledge() }),
    "/memory":       () => {
      queryClient.prefetchQuery({ queryKey: qk.memoryStats(),          queryFn: fetchers.memoryStats });
      queryClient.prefetchQuery({ queryKey: qk.memoryEntries("short_term"), queryFn: () => fetchers.memoryEntries("short_term") });
      queryClient.prefetchQuery({ queryKey: qk.memoryEntries("long_term"),  queryFn: () => fetchers.memoryEntries("long_term") });
      queryClient.prefetchQuery({ queryKey: qk.memoryEntries("preference"), queryFn: () => fetchers.memoryEntries("preference") });
    },
    "/agents":       () => queryClient.prefetchQuery({ queryKey: qk.agents(),      queryFn: fetchers.agents }),
    "/workflows":    () => queryClient.prefetchQuery({ queryKey: qk.workflows(),   queryFn: fetchers.workflows }),
    "/logs":         () => queryClient.prefetchQuery({ queryKey: qk.logs(),        queryFn: () => fetchers.logs() }),
    "/settings":     () => {
      queryClient.prefetchQuery({ queryKey: qk.authMe(),          queryFn: fetchers.authMe });
      queryClient.prefetchQuery({ queryKey: qk.featureFlags(),    queryFn: fetchers.featureFlags });
      queryClient.prefetchQuery({ queryKey: qk.settingsSecrets(), queryFn: fetchers.settingsSecrets });
    },
  };
  const fn = rules[to];
  if (fn) {
    try { fn(); } catch { /* ignore */ }
  }
}

export const prefetchSettingsTab = {
  profile:      () => queryClient.prefetchQuery({ queryKey: qk.authMe(),          queryFn: fetchers.authMe }),
  flags:        () => queryClient.prefetchQuery({ queryKey: qk.featureFlags(),    queryFn: fetchers.featureFlags }),
  keys:         () => queryClient.prefetchQuery({ queryKey: qk.settingsSecrets(), queryFn: fetchers.settingsSecrets }),
  integrations: () => {
    queryClient.prefetchQuery({ queryKey: qk.featureFlags(),    queryFn: fetchers.featureFlags });
    queryClient.prefetchQuery({ queryKey: qk.settingsSecrets(), queryFn: fetchers.settingsSecrets });
    queryClient.prefetchQuery({ queryKey: qk.pushDeadLetter(),  queryFn: fetchers.pushDeadLetter });
  },
  danger:       () => queryClient.prefetchQuery({ queryKey: qk.authMe(),          queryFn: fetchers.authMe }),
};
